import React, { useMemo, useState } from 'react';

import type { CourseProfile, CourseProfileLevel } from '../../types/courseProfile';
import type { SalaTeacherProfile } from '../../../auth/types';
import { EXAM_LEVEL_OPTIONS } from '../../constants/examAiParams';
import { resolveCourseDisplayName } from '../../constants/courseProfileUi';
import {
  createCourseProfileId,
  saveManyCourseProfiles,
} from '../../services/courseProfileStorage';

interface CourseSeedWizardProps {
  open: boolean;
  profile: SalaTeacherProfile;
  onClose: () => void;
  onCreated: () => void;
}

export function CourseSeedWizard({ open, profile, onClose, onCreated }: CourseSeedWizardProps) {
  const [selected, setSelected] = useState<Set<CourseProfileLevel>>(
    () => new Set(EXAM_LEVEL_OPTIONS.map((option) => option.id)),
  );
  const [busy, setBusy] = useState(false);

  const levels = useMemo(() => EXAM_LEVEL_OPTIONS, []);

  if (!open) return null;

  const toggle = (id: CourseProfileLevel) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleCreate = () => {
    if (selected.size === 0) return;
    setBusy(true);
    try {
      const courses: CourseProfile[] = [...selected].map((level) => {
        const draft: CourseProfile = {
          id: createCourseProfileId(),
          displayName: '',
          schoolName: profile.schoolName || '',
          subjectId: profile.subjectId,
          subjectTag: profile.subjectTag,
          level,
          interests: [],
          learningStyle: [],
          strengths: [],
          difficulties: [],
        };
        return {
          ...draft,
          displayName: resolveCourseDisplayName(draft),
        };
      });
      saveManyCourseProfiles(courses);
      onCreated();
      onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div
        className="w-full max-w-lg rounded-2xl border p-6 shadow-xl"
        style={{
          borderColor: 'var(--sala-border-strong)',
          background: 'var(--sala-surface-raised)',
        }}
        role="dialog"
        aria-labelledby="course-seed-title"
      >
        <p className="sala-kicker mb-1">Primeros pasos</p>
        <h2 id="course-seed-title" className="font-display text-xl font-bold">
          Arma tus cursos
        </h2>
        <p className="mt-2 text-sm text-[var(--sala-ink-muted)]">
          Selecciona los niveles que haces. Se crearán con tu asignatura (
          <strong className="text-[var(--sala-ink)]">{profile.subjectTag}</strong>
          ){profile.schoolName ? ` y el colegio «${profile.schoolName}»` : ''}.
        </p>

        <div className="mt-5 grid grid-cols-2 gap-2 sm:grid-cols-4">
          {levels.map((level) => {
            const active = selected.has(level.id);
            return (
              <button
                key={level.id}
                type="button"
                onClick={() => toggle(level.id)}
                className="rounded-xl border px-3 py-2.5 text-left text-xs font-semibold transition"
                style={{
                  borderColor: active ? 'var(--sala-accent)' : 'var(--sala-border-strong)',
                  background: active
                    ? 'color-mix(in srgb, var(--sala-accent) 16%, transparent)'
                    : 'transparent',
                  color: 'var(--sala-ink)',
                }}
              >
                {level.label}
              </button>
            );
          })}
        </div>

        <div className="mt-6 flex flex-wrap justify-end gap-2">
          <button type="button" className="sala-btn-outline text-xs" onClick={onClose} disabled={busy}>
            Ahora no
          </button>
          <button
            type="button"
            className="sala-btn-hero text-xs"
            onClick={handleCreate}
            disabled={busy || selected.size === 0}
          >
            {busy ? 'Creando…' : `Crear ${selected.size} curso${selected.size === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>
    </div>
  );
}
